import {
  DndContext,
  MouseSensor,
  PointerSensor,
  restrictToHorizontalAxis,
  TouchSensor,
  useSensor,
  useSensors,
} from '@github-ui/drag-and-drop'
import {testIdProps} from '@github-ui/test-id-props'
import {useRefObjectAsForwardedRef} from '@primer/react'
import {memo, useMemo, useRef, useState} from 'react'

import {SystemColumnId} from '../../../api/columns/contracts/memex-column'
import {ItemType} from '../../../api/memex-items/item-type'
import {RoadmapDateFieldNone} from '../../../api/view/contracts'
import useIsVisible from '../../../components/board/hooks/use-is-visible'
import {useReorderableRow} from '../../../components/table/row-reordering/reorderable-rows'
import type {TableDataType} from '../../../components/table/table-data-type'
import {RowContext} from '../../../components/table/table-row'
import {rowTestId} from '../../../components/table/test-identifiers'
import type {ItemDataType} from '../../../components/table/use-item-data'
import {parseTitleNumber} from '../../../helpers/parsing'
import {getColumnModel} from '../../../hooks/tanstack-table/helpers/column-api'
import {isRoadmapPillAreaColumnId, RowDragHandleColumnId} from '../../../hooks/tanstack-table/helpers/column-ids'
import {getVisibleCells} from '../../../hooks/tanstack-table/helpers/row-api'
import type {Row} from '../../../hooks/tanstack-table/helpers/types'
import {useEnabledFeatures} from '../../../hooks/use-enabled-features'
import {useRoadmapSettings} from '../../../hooks/use-roadmap-settings'
import {useRoadmapTableIsNarrow} from '../../../hooks/use-roadmap-table-is-narrow'
import {useRootElement} from '../../../hooks/use-root-element'
import {EmptyValue, withValue} from '../../../models/column-value'
import {useMouseState} from '../hooks/use-mouse-state'
import {RoadmapPillAreaFocusProvider} from '../hooks/use-roadmap-pill-area-focus'
import {RoadmapPillArea} from './roadmap-pill-area'
import {RoadmapNumberCell, RoadmapTimeSpanCells, RoadmapTitleCell} from './roadmap-table-cells'
import {RoadmapRow} from './roadmap-table-layout'

type RoadmapItemProps = {
  row: Row<TableDataType>
  itemData: ItemDataType
}

export const RoadmapItem = memo(function RoadmapItem({row, itemData}: RoadmapItemProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const {isVisible} = useIsVisible({ref})
  const [isDraggingPill, setIsDraggingPill] = useState(false)
  const {showDateFields} = useRoadmapSettings()
  const {dateFields} = useRoadmapSettings()
  const {memex_touch_to_drag} = useEnabledFeatures()

  const {mouseState, ...mouseProps} = useMouseState()
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250, // delay in ms before the drag starts
      tolerance: 5,
      distance: 2, // measured in pixels
      autoscrollEnabled: true,
    },
  })
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 2, // constraint to ensure that a click does not get registered as drag
      autoscrollEnabled: false,
    },
  })
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 2, // measured in pixels
    },
  })
  const differentiateSensors = memex_touch_to_drag
  const sensorsList = differentiateSensors ? [touchSensor, mouseSensor] : [pointerSensor]
  const sensors = useSensors(...sensorsList)
  const modifiers = [restrictToHorizontalAxis]

  const item = row.original
  const titleHtml = item.getHtmlTitle()

  const url = item.getUrl()
  const columnData = item.columns

  const titleValue = columnData[SystemColumnId.Title]
  const titleColumnValue = useMemo(() => (titleValue ? withValue(titleValue) : EmptyValue), [titleValue])
  const assignees = columnData[SystemColumnId.Assignees]
  const number = parseTitleNumber(titleValue)

  const rootElement = useRootElement()

  const numberCell = getVisibleCells(row).find(c => c.column.id === RowDragHandleColumnId)

  const titleCell = getVisibleCells(row).find(c => c.column.id === SystemColumnId.Title)

  const startCell = showDateFields
    ? getVisibleCells(row).find(
        c => dateFields[0] !== RoadmapDateFieldNone && getColumnModel(c.column)?.id === dateFields[0]?.id,
      )
    : undefined

  const endCell = showDateFields
    ? getVisibleCells(row).find(
        c => dateFields[1] !== RoadmapDateFieldNone && getColumnModel(c.column)?.id === dateFields[1]?.id,
      )
    : undefined

  const pill = getVisibleCells(row).find(c => isRoadmapPillAreaColumnId(c.column.id))

  const dndAccessibility = useMemo(
    () => ({
      container: rootElement,
    }),
    [rootElement],
  )

  const {setNodeRef, setActivatorNodeRef, listeners, isSorting, isDragging, isDropped} = useReorderableRow(row)

  const context = useMemo(
    () => ({
      rowNumber: row.index + 1,
      setSortActivatorNodeRef: setActivatorNodeRef,
      sortListeners: listeners,
    }),
    [row.index, setActivatorNodeRef, listeners],
  )
  useRefObjectAsForwardedRef(setNodeRef, ref)

  const {isTableNarrow} = useRoadmapTableIsNarrow()

  // Return early with an empty roadmap row placeholder if not within the viewport or if title cell has not loaded
  // Unlike in table view, we don't need to check `isDragging` because the `useReorderableRow` hook is still called
  // and that's all that's necessary for dnd-kit to provide the drop data.
  if (!isVisible || !titleCell || !pill || !numberCell) return <RoadmapRow aria-hidden="true" ref={ref} />

  return (
    <RoadmapRow
      ref={ref}
      row={row}
      {...testIdProps(rowTestId(row.index))}
      isDraggingPill={isDraggingPill}
      isHighlighted={isDragging || isDropped}
      {...(isSorting ? {} : mouseProps)}
      className={isTableNarrow ? 'narrow-roadmap-table' : ''}
    >
      <RowContext value={context}>
        <RoadmapNumberCell cell={numberCell} focusCellElement={itemData.focusCellElement} isDragging={isSorting} />
        <RoadmapTitleCell
          cell={titleCell}
          focusCellElement={itemData.focusCellElement}
          number={number}
          isLastCell={!showDateFields || (!startCell && !endCell)}
        />
        {showDateFields && (
          <RoadmapTimeSpanCells
            startCell={startCell}
            endCell={endCell}
            focusCellElement={itemData.focusCellElement}
            number={number}
          />
        )}
      </RowContext>
      <DndContext sensors={sensors} autoScroll={false} modifiers={modifiers} accessibility={dndAccessibility}>
        <RoadmapPillAreaFocusProvider cell={pill} rowRef={ref}>
          <RoadmapPillArea
            titleHtml={titleHtml}
            renderLink={item.contentType !== ItemType.RedactedItem}
            url={url}
            titleColumnValue={titleColumnValue}
            number={number}
            assignees={assignees}
            index={row.index}
            item={item}
            mouseState={mouseState}
            setIsDragging={setIsDraggingPill}
          />
        </RoadmapPillAreaFocusProvider>
      </DndContext>
    </RoadmapRow>
  )
})
